/**
 * PersonaTrendChart — the « Évolution » panel's chart (§4.5.3, mock `chartSVG`).
 *
 * One thin line per configuration (colour = engine) plus the thick accent line: the
 * 3-day rolling mean over every configuration in scope. Clicking a day selects it —
 * that is the only way to reach « Runs du <jour> » for a day other than the last.
 *
 * Reads `ConversationPersonaRollup.trend` only (tier 0, zero extra requests). The
 * mock's « Indicateur : Présence / Gagné » toggle is NOT here: `trend` carries
 * `presence` per day and no win counter, so a « Gagné » series would be invented.
 *
 * A day with no launch is never a zero. The line is DRAWN across it (two launches three
 * days apart must read as one series, not two orphan dots) but carries no marker dot and
 * no hit area there, so the connector cannot be mistaken for a measurement.
 */

import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ConversationPersonaRollup } from '@traaker/shared';
import { engineDisplay, engineMeta, formatDayShort, pct, splitCellKey } from './personaFormat';

const SERIES_DASH = '4 3';

export interface PersonaTrendChartProps {
  trend: ConversationPersonaRollup['trend'];
  /** Window length in days (7 / 30 / 90). */
  windowDays: number;
  /** 'YYYY-MM-DD' — highlighted column. */
  selectedDay: string | null;
  onSelectDay: (day: string) => void;
  /** zone key → human label, from the persona's `zoneMeta`. */
  zoneLabels?: Record<string, string>;
  /** Legend label of the thick rolling-mean series (the panel owns the copy). */
  meanLabel: string;
}

function dayAxis(windowDays: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = windowDays - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

export function PersonaTrendChart({
  trend,
  windowDays,
  selectedDay,
  onSelectDay,
  zoneLabels = {},
  meanLabel,
}: PersonaTrendChartProps) {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const days = dayAxis(windowDays);
  const cellKeys = Object.keys(trend);

  // cellKey → day → { presence, n }
  const byCell = new Map<string, Map<string, { presence: number | null; n: number }>>();
  for (const ck of cellKeys) {
    const m = new Map<string, { presence: number | null; n: number }>();
    for (const p of trend[ck] ?? []) m.set(p.d, { presence: p.presence, n: p.n });
    byCell.set(ck, m);
  }

  // Aggregate day presence, run-weighted across configurations (a day with no run is a gap).
  const agg = days.map((d) => {
    let num = 0;
    let den = 0;
    for (const ck of cellKeys) {
      const p = byCell.get(ck)?.get(d);
      if (!p || p.presence == null || p.n <= 0) continue;
      num += p.presence * p.n;
      den += p.n;
    }
    return den > 0 ? num / den : null;
  });

  // 3-day rolling mean of the aggregate — the mock's thick accent series.
  // Only smooth days that actually ran: the window looks 2 days BACK, so without
  // the `agg[i] == null` guard the mean stayed non-null for 2 days past the last
  // run and painted a line + a marker dot on days with no launch, which the hit
  // area below (gated on `agg[i]`) then refused to open (fixed 2026-09-28).
  const mean = agg.map((v, i) => {
    if (v == null) return null;
    const slice = agg.slice(Math.max(0, i - 2), i + 1).filter((x): x is number => x != null);
    return slice.length ? slice.reduce((a, b) => a + b, 0) / slice.length : null;
  });

  const series = cellKeys.map((ck) => {
    const { engine, language, zone } = splitCellKey(ck);
    return {
      cellKey: ck,
      label: `${engineDisplay(engine)} · ${zoneLabels[zone] ?? zone} (${language})`,
      color: engineMeta(engine).color,
      dash: SERIES_DASH,
      pts: days.map((d) => byCell.get(ck)?.get(d)?.presence ?? null),
    };
  }).filter((s) => s.pts.some((v) => v != null));

  const all = [...series.flatMap((s) => s.pts), ...mean].filter((v): v is number => v != null);
  if (all.length === 0) return null;
  const top = Math.min(1, Math.max(0.2, Math.ceil((Math.max(...all) + 0.05) * 5) / 5));

  const W = 1100;
  const H = 280;
  const L = 48;
  const R = 14;
  const T = 16;
  const B = 34;
  const iw = W - L - R;
  const ih = H - T - B;
  const step = days.length > 1 ? iw / (days.length - 1) : 0;
  const x = (i: number) => L + step * i;
  const y = (v: number) => T + ih * (1 - v / top);
  const bandW = days.length > 1 ? step : iw;

  // Joins across days with no launch (2026-09-28). Cadence is every few days, so breaking
  // the line at every un-run day left each launch an isolated dot with nothing tying it to
  // the previous one — and a per-cell series, which has no marker circle, rendered a
  // single-day launch as a path with one `M` and no `L`: invisible. The segment is a
  // CONNECTOR, not data: the marker dots and the hit areas stay gated on a day that ran,
  // so an un-run day still has no point and no tooltip. Interpolating between two
  // measurements is fine; drawing a zero is not, and that is what the gap rule forbade.
  const path = (pts: (number | null)[]) => {
    let d = '';
    pts.forEach((v, i) => {
      if (v == null) return;
      d += `${d ? 'L' : 'M'}${x(i).toFixed(1)} ${y(v).toFixed(1)} `;
    });
    return d;
  };

  // Keep the axis readable at 30 / 90 days: ~7 labels max.
  const labelEvery = Math.max(1, Math.ceil(days.length / 7));

  return (
    <Box>
      <Box component="svg" viewBox={`0 0 ${W} ${H}`} sx={{ width: '100%', display: 'block' }} role="img">
        {[0, 1, 2, 3, 4, 5].map((k) => {
          const v = (top * k) / 5;
          return (
            <g key={k}>
              <line x1={L} x2={W - R} y1={y(v)} y2={y(v)} stroke={theme.palette.divider} />
              <text x={L - 10} y={y(v) + 4} textAnchor="end" fontSize={11} fill={theme.palette.text.secondary}>
                {Math.round(v * 100)} %
              </text>
            </g>
          );
        })}

        {/* selected-day band */}
        {selectedDay && days.includes(selectedDay) && (
          <rect
            x={x(days.indexOf(selectedDay)) - bandW / 2}
            y={T - 6}
            width={bandW}
            height={ih + 12}
            rx={4}
            fill={theme.palette.primary.main}
            opacity={0.12}
          />
        )}

        {days.map((d, i) => (
          i % labelEvery === 0 || i === days.length - 1 ? (
            <text
              key={d}
              x={x(i)}
              y={H - 12}
              textAnchor="middle"
              fontSize={11}
              fill={theme.palette.text.secondary}
            >
              {formatDayShort(d, i18n.language)}
            </text>
          ) : null
        ))}

        {series.map((s) => (
          <path
            key={s.cellKey}
            d={path(s.pts)}
            fill="none"
            stroke={s.color}
            strokeWidth={1.6}
            strokeDasharray={s.dash || undefined}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ))}
        <path
          d={path(mean)}
          fill="none"
          stroke="#374151"
          strokeWidth={3.2}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
        {mean.map((v, i) => (v == null ? null : (
          <circle
            key={days[i]}
            cx={x(i)}
            cy={y(v)}
            r={days[i] === selectedDay ? 5 : 3}
            fill="#374151"
            stroke={theme.palette.background.paper}
            strokeWidth={1.5}
          />
        )))}

        {/* Hit areas — only a day that actually ran is clickable. */}
        {days.map((d, i) => {
          const hasData = agg[i] != null;
          return (
            <Tooltip
              key={`hit-${d}`}
              title={
                <Box>
                  <Typography variant="caption" fontWeight={700} display="block">
                    {formatDayShort(d, i18n.language)}
                  </Typography>
                  {hasData ? (
                    <>
                      <Typography variant="caption" display="block">{pct(agg[i])}</Typography>
                      {series.map((s) => (s.pts[i] == null ? null : (
                        <Typography key={s.cellKey} variant="caption" display="block">
                          {s.label} · {pct(s.pts[i])}
                        </Typography>
                      )))}
                    </>
                  ) : null}
                </Box>
              }
              followCursor
            >
              <rect
                x={x(i) - bandW / 2}
                y={T - 6}
                width={bandW}
                height={ih + 12}
                fill="transparent"
                style={{ cursor: hasData ? 'pointer' : 'default' }}
                onClick={hasData ? () => onSelectDay(d) : undefined}
              />
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Box aria-hidden sx={{ width: 16, height: 3, borderRadius: 2, bgcolor: '#374151' }} />
          <Typography variant="caption" color="text.secondary">{meanLabel}</Typography>
        </Box>
        {series.map((s) => (
          <Box key={s.cellKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
            <Box aria-hidden sx={{ width: 16, height: 2, bgcolor: s.color }} />
            <Typography variant="caption" color="text.secondary">{s.label}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
